import { useMemo } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { QUESTION } from '@constants/terminology';
import { ColumnDef, Table } from '@g17eco/molecules/table';
import { ConfigurationType } from '../types';
import { getDisplayedInputOverrides, getDisplayedOverrides } from '@features/question-management/displayUtils';

type UtrTagMap = Record<string, string[]>;

interface SelectedMetricsModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedQuestions: BulkActionUtr[];
  activeTab: ConfigurationType;
  rootInitiativeUtrMap: Map<string, InitiativeUniversalTracker>;
  utrTagMap: UtrTagMap;
}

const nameColumn: ColumnDef<BulkActionUtr> = {
  header: `${QUESTION.CAPITALIZED_SINGULAR} name`,
  cell: ({ row }) => row.original.name,
};

const valueTypeColumn: ColumnDef<BulkActionUtr> = {
  header: 'Value type',
  cell: ({ row }) => row.original.valueType ?? '-',
};

const getColumns = ({
  activeTab,
  rootInitiativeUtrMap,
  utrTagMap,
}: {
  activeTab: ConfigurationType;
  rootInitiativeUtrMap: Map<string, InitiativeUniversalTracker>;
  utrTagMap: UtrTagMap;
}): ColumnDef<BulkActionUtr>[] => {
  switch (activeTab) {
    case ConfigurationType.InputOverrides:
      return [
        nameColumn,
        valueTypeColumn,
        {
          header: 'Decimals',
          cell: ({ row }) => getDisplayedInputOverrides(row.original, rootInitiativeUtrMap).decimal,
        },
        {
          header: 'Unit',
          cell: ({ row }) => getDisplayedInputOverrides(row.original, rootInitiativeUtrMap).unit,
        },
        {
          header: 'Number scale',
          cell: ({ row }) => getDisplayedInputOverrides(row.original, rootInitiativeUtrMap).numberScale,
        },
      ];
    case ConfigurationType.MetricOverrides:
      return [
        nameColumn,
        valueTypeColumn,
        {
          header: 'Overrides',
          cell: ({ row }) => getDisplayedOverrides(row.original, rootInitiativeUtrMap),
        },
      ];
    case ConfigurationType.Tags:
      return [
        nameColumn,
        {
          header: 'Tags',
          cell: ({ row }) => {
            const tags = utrTagMap[row.original._id];
            return tags?.length ? tags.join(', ') : '-';
          },
        },
      ];
    default:
      return [nameColumn, valueTypeColumn];
  }
};

export const SelectedMetricsModal = ({
  isOpen,
  toggle,
  selectedQuestions,
  activeTab,
  rootInitiativeUtrMap,
  utrTagMap,
}: SelectedMetricsModalProps) => {
  const columns = useMemo(
    () => getColumns({ activeTab, rootInitiativeUtrMap, utrTagMap }),
    [activeTab, rootInitiativeUtrMap, utrTagMap]
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size='lg'>
      <ModalHeader toggle={toggle}>
        Selected {QUESTION.PLURAL} ({selectedQuestions.length})
      </ModalHeader>
      <ModalBody>
        <Table columns={columns} data={selectedQuestions} pageSize={selectedQuestions.length} />
      </ModalBody>
    </Modal>
  );
};
